import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowUpRight, ArrowDownRight, RefreshCw, Search, Filter,
  Calendar, Download, DollarSign, TrendingUp,
  CreditCard, Wallet, Eye, ChevronLeft, ChevronRight, X, FileText,
  Sparkles, Shield, AlertTriangle, Gamepad2, HelpCircle, ShoppingCart
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { exportToCSV, exportToPDF, formatDateForExport, formatCurrencyForExport } from '../utils/exportUtils';
import { useToast } from '../contexts/ToastContext';
import { adminService } from '../services/adminService';
import { Transaction } from '../types';
import './Transactions.css';

type TransactionType =
  | 'game_stake' | 'game_win' | 'ad_reward' | 'quiz_bonus'
  | 'withdrawal' | 'deposit' | 'reveal_tool_purchase';

type TransactionStatus = 'completed' | 'pending' | 'failed' | 'reversed';

const TRANSACTION_TYPES: { value: TransactionType; label: string }[] = [
  { value: 'game_stake', label: 'Game Stake' },
  { value: 'game_win', label: 'Game Win' },
  { value: 'ad_reward', label: 'Ad Reward' },
  { value: 'quiz_bonus', label: 'Quiz Bonus' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'reveal_tool_purchase', label: 'Reveal Tool' },
];

const TRANSACTION_STATUSES: { value: TransactionStatus; label: string }[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'reversed', label: 'Reversed' },
];

const PAGE_SIZE = 20;

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = useToast();

  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalVolume: 0,
    completedCount: 0,
    pendingCount: 0,
  });

  const txExportColumns = [
    { key: 'id', header: 'ID' },
    { key: 'user_id', header: 'User ID' },
    { key: 'type', header: 'Type', formatter: (v: string) => getTypeLabel(v) },
    { key: 'amount', header: 'Amount', formatter: (v: number) => formatCurrencyForExport(v, '₦') },
    { key: 'currency_code', header: 'Currency' },
    { key: 'status', header: 'Status' },
    { key: 'description', header: 'Description' },
    { key: 'reference', header: 'Reference' },
    { key: 'created_at', header: 'Date', formatter: formatDateForExport },
  ];

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, any> = {
        page,
        page_size: PAGE_SIZE,
      };
      if (typeFilter) params.type_filter = typeFilter;
      if (statusFilter) params.status_filter = statusFilter;

      const response = await adminService.getTransactions(params);
      const txList = response.transactions || [];

      setTransactions(txList);
      setTotal(response.total ?? txList.length);

      const volume = txList.reduce((acc: number, tx: Transaction) => acc + Math.abs(tx.amount), 0);
      const completed = txList.filter((tx: Transaction) => tx.status === 'completed').length;
      const pending = txList.filter((tx: Transaction) => tx.status === 'pending').length;

      setStats({
        totalTransactions: response.total ?? txList.length,
        totalVolume: volume,
        completedCount: completed,
        pendingCount: pending,
      });
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load transactions';
      setError(message);
      toast.error('Load Failed', message);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, statusFilter, toast]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      loadTransactions();
    }, 400);
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleExportCSV = () => {
    if (transactions.length === 0) {
      toast.warning('No Data', 'No transactions to export');
      return;
    }
    exportToCSV(filteredTransactions, txExportColumns, 'transactions_export');
    toast.success('Export Complete', 'Transactions exported to CSV');
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    if (transactions.length === 0) {
      toast.warning('No Data', 'No transactions to export');
      return;
    }
    exportToPDF(filteredTransactions, txExportColumns, 'Transaction History Report', 'transactions_report');
    toast.success('Export Complete', 'PDF report generated');
    setShowExportMenu(false);
  };

  const filteredTransactions = search
    ? transactions.filter((tx) => {
        const q = search.toLowerCase();
        return (
          (tx.reference && tx.reference.toLowerCase().includes(q)) ||
          (tx.description && tx.description.toLowerCase().includes(q)) ||
          tx.id.toString().includes(q) ||
          tx.user_id.toString().includes(q)
        );
      })
    : transactions;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit': return <ArrowDownRight className="type-icon deposit" />;
      case 'withdrawal': return <ArrowUpRight className="type-icon withdrawal" />;
      case 'game_win': return <TrendingUp className="type-icon win" />;
      case 'game_stake': return <Gamepad2 className="type-icon stake" />;
      case 'ad_reward': return <Sparkles className="type-icon reward" />;
      case 'quiz_bonus': return <HelpCircle className="type-icon bonus" />;
      case 'reveal_tool_purchase': return <ShoppingCart className="type-icon purchase" />;
      default: return <Wallet className="type-icon" />;
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'withdrawal': return 'Withdrawal';
      case 'game_win': return 'Game Win';
      case 'game_stake': return 'Game Stake';
      case 'ad_reward': return 'Ad Reward';
      case 'quiz_bonus': return 'Quiz Bonus';
      case 'reveal_tool_purchase': return 'Reveal Tool';
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
  };

  const isDebit = (type: string) =>
    ['withdrawal', 'game_stake', 'reveal_tool_purchase'].includes(type);

  const formatAmount = (tx: Transaction) => {
    const symbol = tx.currency_code === 'NGN' ? '₦' : tx.currency_code || '₦';
    const prefix = isDebit(tx.type) ? '-' : '+';
    return `${prefix}${symbol === '₦' ? '₦' : symbol + ' '}${Math.abs(tx.amount).toLocaleString()}`;
  };

  return (
    <div className="transactions-page">
      {/* Header */}
      <div className="tx-header">
        <div className="header-content">
          <h1><CreditCard size={28} /> Transactions</h1>
          <p className="header-subtitle">Monitor all platform transactions and activity</p>
        </div>
        <div className="header-actions">
          <button onClick={loadTransactions} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <div className="export-dropdown" ref={exportRef}>
            <button
              className="export-btn"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <Download size={18} />
              Export
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={handleExportCSV}>
                  <FileText size={16} /> Export as CSV
                </button>
                <button onClick={handleExportPDF}>
                  <FileText size={16} /> Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="tx-stats">
        <div className="stat-card">
          <div className="stat-icon volume">
            <CreditCard size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalTransactions.toLocaleString()}</span>
            <span className="stat-label">Total Transactions</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon deposits">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">₦{stats.totalVolume.toLocaleString()}</span>
            <span className="stat-label">Total Volume</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon withdrawals">
            <Shield size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.completedCount}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by reference, description, ID..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button className="clear-search" onClick={() => { setSearch(''); setPage(1); }}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={typeFilter} onChange={(e) => handleTypeFilterChange(e.target.value)}>
            <option value="">All Types</option>
            {TRANSACTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)}>
            <option value="">All Status</option>
            {TRANSACTION_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="tx-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={32} />
            <span>Loading transactions...</span>
          </div>
        ) : error ? (
          <div className="error-state">
            <AlertTriangle size={48} />
            <h3>Failed to load transactions</h3>
            <p>{error}</p>
            <button onClick={loadTransactions} className="retry-btn">
              <RefreshCw size={18} />
              Retry
            </button>
          </div>
        ) : (
          <>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>User ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Reference</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id} onClick={() => setSelectedTx(tx)} style={{ cursor: 'pointer' }}>
                    <td className="id-cell">#{tx.id}</td>
                    <td>
                      <div className="type-cell">
                        {getTypeIcon(tx.type)}
                        <span className="type-label">{getTypeLabel(tx.type)}</span>
                      </div>
                    </td>
                    <td>
                      <a
                        href={`/dashboard/users/${tx.user_id}`}
                        className="user-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        #{tx.user_id}
                      </a>
                    </td>
                    <td>
                      <span className={`amount ${isDebit(tx.type) ? 'negative' : 'positive'}`}>
                        {formatAmount(tx)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${tx.status}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="description-cell">{tx.description || '—'}</td>
                    <td className="reference-cell">{tx.reference || '—'}</td>
                    <td className="date-cell">
                      {formatDistanceToNow(new Date(tx.created_at), { addSuffix: true })}
                    </td>
                    <td>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedTx(tx); }}
                        className="view-btn"
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <CreditCard size={48} />
                <h3>No transactions found</h3>
                <p>Try adjusting your filters or search terms</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && total > 0 && (
        <div className="pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="page-btn"
          >
            <ChevronLeft size={18} /> Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages} ({total.toLocaleString()} total)
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="page-btn"
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTx && (
        <div className="modal-overlay" onClick={() => setSelectedTx(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transaction Details</h3>
              <button className="modal-close" onClick={() => setSelectedTx(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="tx-detail-header">
                {getTypeIcon(selectedTx.type)}
                <div className="tx-detail-info">
                  <h2>{getTypeLabel(selectedTx.type)}</h2>
                  <span className={`status-badge large ${selectedTx.status}`}>
                    {selectedTx.status}
                  </span>
                </div>
              </div>

              <div className="tx-amount-display">
                <span className={`amount ${isDebit(selectedTx.type) ? 'negative' : 'positive'}`}>
                  {formatAmount(selectedTx)}
                </span>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Transaction ID</span>
                  <span className="detail-value">#{selectedTx.id}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">User ID</span>
                  <span className="detail-value">
                    <a
                      href={`/dashboard/users/${selectedTx.user_id}`}
                      className="user-link"
                    >
                      #{selectedTx.user_id}
                    </a>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Currency</span>
                  <span className="detail-value">{selectedTx.currency_code || 'NGN'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">{format(new Date(selectedTx.created_at), 'PPpp')}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Reference</span>
                  <span className="detail-value">{selectedTx.reference || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">
                    <span className={`status-badge ${selectedTx.status}`}>{selectedTx.status}</span>
                  </span>
                </div>
              </div>

              {selectedTx.description && (
                <div className="tx-description">
                  <span className="detail-label">Description</span>
                  <p>{selectedTx.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
